const ROW_VIEW = 'rows';
const CARD_VIEW = 'cards';
const MOBILE_CARD_VIEW_QUERY = '(max-width: 575.98px)';

function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) {
        element.className = className;
    }
    if (text !== undefined && text !== null) {
        element.textContent = String(text);
    }
    return element;
}

function valueAt(record, path) {
    return String(path || '')
        .split('.')
        .filter(Boolean)
        .reduce((value, segment) => value?.[segment], record);
}

function setValueAt(record, path, value) {
    const segments = String(path || '').split('.').filter(Boolean);
    let target = record;
    segments.forEach((segment, index) => {
        if (index === segments.length - 1) {
            target[segment] = value;
            return;
        }
        target[segment] ||= {};
        target = target[segment];
    });
}

function dataTableApi(table) {
    try {
        if (window.jQuery?.fn?.DataTable?.isDataTable(table)) {
            return window.jQuery(table).DataTable();
        }
    } catch {
        // The native-table fallback keeps the view usable during initialization.
    }
    return null;
}

function tableRecords(table) {
    const api = dataTableApi(table);
    if (api) {
        const records = [];
        api.rows({ page: 'current', search: 'applied' }).every(function () {
            records.push({ data: this.data(), node: this.node() });
        });
        return records;
    }

    const headers = Array.from(table.tHead?.rows[0]?.cells ?? []);
    return Array.from(table.tBodies[0]?.rows ?? [])
        .filter(row => !row.querySelector('td.dataTables_empty, td.dt-empty'))
        .map(row => {
            const data = {};
            Array.from(row.cells).forEach((cell, index) => {
                setValueAt(data, headers[index]?.dataset.data, cell.textContent.trim());
            });
            return { data, node: row };
        });
}

function normalizedText(value) {
    if (typeof value === 'string') {
        return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }
    return '';
}

function personLocation(person) {
    const location = person.location || {};
    return [
        location.addressLine1,
        location.addressLine2,
        location.locality,
        location.administrativeArea,
        location.postalCode,
        location.country,
    ].map(normalizedText).filter(Boolean).join(', ');
}

function personTags(person) {
    return Array.isArray(person.personTags)
        ? person.personTags.map(tag => normalizedText(tag?.name)).filter(Boolean)
        : [];
}

function organizationTags(organization) {
    return Array.isArray(organization.organizationTags)
        ? organization.organizationTags.map(tag => normalizedText(tag?.name)).filter(Boolean)
        : [];
}

function caseTags(caseRecord) {
    return Array.isArray(caseRecord.tags)
        ? caseRecord.tags.map(tag => normalizedText(tag?.name)).filter(Boolean)
        : [];
}

function caseParticipants(caseRecord) {
    return Array.isArray(caseRecord.participants)
        ? caseRecord.participants
            .map(participant => normalizedText(participant?.displayName))
            .filter(Boolean)
        : [];
}

function caseOrganizations(caseRecord) {
    return Array.isArray(caseRecord.organizations)
        ? caseRecord.organizations
            .map(relationship => normalizedText(relationship?.name))
            .filter(Boolean)
        : [];
}

function caseArticles(caseRecord) {
    return Array.isArray(caseRecord.articles)
        ? caseRecord.articles.map(record => {
            return normalizedText(record?.name);
        }).filter(Boolean)
        : [];
}

function columnLabel(header) {
    return normalizedText(header?.querySelector('.dt-column-title')?.textContent)
        || normalizedText(header?.textContent);
}

function columnKey(header, path) {
    if (header?.classList.contains('personTags')) {
        return 'personTags';
    }
    if (header?.classList.contains('organizationTags')) {
        return 'organizationTags';
    }
    if (header?.classList.contains('caseParticipantPeople')) {
        return 'caseParticipantPeople';
    }
    if (header?.classList.contains('caseRelationshipOrganizations')) {
        return 'caseRelationshipOrganizations';
    }
    if (header?.classList.contains('caseArticles')) {
        return 'caseArticles';
    }
    if (header?.classList.contains('caseArticleAgreements')) return 'caseArticleAgreements';
    if (header?.classList.contains('caseArticleNumbers')) return 'caseArticleNumbers';
    if (header?.classList.contains('caseTags')) {
        return 'caseTags';
    }
    return path;
}

function visibleCardColumns(table) {
    const api = dataTableApi(table);
    const columns = [];

    if (api?.columns) {
        api.columns().every(function () {
            const header = this.header();
            if (!header?.classList.contains('row-selector') &&
                !header?.classList.contains('responsive-control') &&
                this.visible()) {
                const path = normalizedText(header.dataset.data);
                columns.push({ key: columnKey(header, path), label: columnLabel(header), path });
            }
        });
        return columns;
    }

    Array.from(table.tHead?.rows[0]?.cells ?? []).forEach(header => {
        if (!header.classList.contains('row-selector') &&
            !header.classList.contains('responsive-control') &&
            !header.classList.contains('no-visible')) {
            const path = normalizedText(header.dataset.data);
            columns.push({ key: columnKey(header, path), label: columnLabel(header), path });
        }
    });
    return columns;
}

function hasCardColumn(columns, key) {
    return columns.some(column => column.key === key);
}

function cardValue(person, column) {
    if (column.key === 'personTags') {
        return personTags(person).join(', ');
    }
    if (column.key === 'organizationTags') {
        return organizationTags(person).join(', ');
    }
    if (column.key === 'caseParticipantPeople') {
        return caseParticipants(person).join(', ');
    }
    if (column.key === 'caseRelationshipOrganizations') {
        return caseOrganizations(person).join(', ');
    }
    if (column.key === 'caseArticles') {
        return caseArticles(person).join(', ');
    }
    if (column.key === 'caseArticleAgreements')
        return (person.articles || []).map(record => normalizedText(record.agreementName)).filter(Boolean).join(', ');
    if (column.key === 'caseArticleNumbers')
        return (person.articles || []).map(record => normalizedText(record.number)).filter(Boolean).join(', ');
    if (column.key === 'caseTags') {
        return caseTags(person).join(', ');
    }

    const value = valueAt(person, column.path);
    if (Array.isArray(value)) {
        return value
            .map(item => normalizedText(item?.name) || normalizedText(item))
            .filter(Boolean)
            .join(', ');
    }
    if (value && typeof value === 'object') {
        return normalizedText(value.name) || normalizedText(value.label);
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    return normalizedText(value);
}

function contactLink(icon, label, href) {
    const link = createElement('a', 'us-person-card__contact');
    link.href = href;
    const glyph = createElement('i', `bi ${icon}`);
    glyph.setAttribute('aria-hidden', 'true');
    link.append(glyph, createElement('span', '', label));
    return link;
}

function createPersonCard(person, rowNode, options, columns) {
    const personId = normalizedText(person.personId);
    const displayName = normalizedText(person.displayName) || options.unnamedLabel;
    const profileUrl = `/People/Edit?id=${encodeURIComponent(personId)}`;
    const card = createElement('article', 'us-person-card');

    const selection = createElement('label', 'us-person-card__selection');
    const selectionInput = createElement('input', 'form-check-input');
    selectionInput.type = 'checkbox';
    selectionInput.setAttribute('aria-label', options.selectTemplate.replace('{0}', displayName));
    const rowCheckbox = rowNode?.querySelector('.row-selector input, .form-check input');
    selectionInput.checked = Boolean(rowCheckbox?.checked);
    card.classList.toggle('is-selected', selectionInput.checked);
    selectionInput.addEventListener('change', () => {
        if (rowCheckbox && rowCheckbox.checked !== selectionInput.checked) {
            rowCheckbox.click();
        }
        card.classList.toggle('is-selected', selectionInput.checked);
    });
    selection.append(selectionInput);

    const showPhoto = hasCardColumn(columns, 'photoUri');
    const photoLink = createElement('a', 'us-person-card__photo-link');
    if (showPhoto) {
        photoLink.href = profileUrl;
        photoLink.setAttribute('aria-label', displayName);
        const photo = createElement('img', 'us-person-card__photo');
        photo.src = normalizedText(person.photoUri) || '/img/profile/profile-11.webp';
        photo.alt = '';
        photo.loading = 'lazy';
        photoLink.append(photo);
    }

    const heading = createElement('div', 'us-person-card__heading');
    const number = normalizedText(person.number);
    if (number && hasCardColumn(columns, 'number')) {
        heading.append(createElement('p', 'us-person-card__number', number));
    }
    if (hasCardColumn(columns, 'displayName')) {
        const name = createElement('a', 'us-person-card__name', displayName);
        name.href = profileUrl;
        heading.append(name);
    }

    const credentials = normalizedText(person.credentialAbbreviations);
    if (credentials && hasCardColumn(columns, 'credentialAbbreviations')) {
        heading.append(createElement('p', 'us-person-card__credentials', credentials));
    }

    const statusName = normalizedText(valueAt(person, 'status.name'));
    if (statusName && hasCardColumn(columns, 'status.name')) {
        const status = createElement('span', 'us-person-card__status', statusName);
        status.dataset.active = String(valueAt(person, 'status.active') !== false);
        heading.append(status);
    }

    const header = createElement('header', 'us-person-card__header');
    header.classList.toggle('us-person-card__header--no-photo', !showPhoto);
    header.append(selection);
    if (showPhoto) {
        header.append(photoLink);
    }
    if (heading.childElementCount) {
        header.append(heading);
    }

    const contact = createElement('div', 'us-person-card__contacts');
    const email = normalizedText(valueAt(person, 'contact.email'));
    const phone = normalizedText(valueAt(person, 'contact.phone'));
    const mobile = normalizedText(valueAt(person, 'contact.mobile'));
    const visibleLocation = {
        addressLine1: hasCardColumn(columns, 'location.addressLine1') ? person.location?.addressLine1 : null,
        addressLine2: hasCardColumn(columns, 'location.addressLine2') ? person.location?.addressLine2 : null,
        locality: hasCardColumn(columns, 'location.locality') ? person.location?.locality : null,
        administrativeArea: hasCardColumn(columns, 'location.administrativeArea') ? person.location?.administrativeArea : null,
        postalCode: hasCardColumn(columns, 'location.postalCode') ? person.location?.postalCode : null,
        country: hasCardColumn(columns, 'location.country') ? person.location?.country : null,
    };
    const location = personLocation({ location: visibleLocation });
    const hasVisibleContact = hasCardColumn(columns, 'contact.email')
        || hasCardColumn(columns, 'contact.phone')
        || hasCardColumn(columns, 'contact.mobile')
        || Object.values(visibleLocation).some(Boolean);
    if (email && hasCardColumn(columns, 'contact.email')) {
        contact.append(contactLink('bi-envelope', email, `mailto:${email}`));
    }
    if (phone && hasCardColumn(columns, 'contact.phone')) {
        contact.append(contactLink('bi-telephone', phone, `tel:${phone.replace(/[^+\d]/g, '')}`));
    }
    if (mobile && hasCardColumn(columns, 'contact.mobile')) {
        contact.append(contactLink('bi-phone', mobile, `tel:${mobile.replace(/[^+\d]/g, '')}`));
    }
    if (location) {
        const locationLine = createElement('p', 'us-person-card__contact');
        const glyph = createElement('i', 'bi bi-geo-alt');
        glyph.setAttribute('aria-hidden', 'true');
        locationLine.append(glyph, createElement('span', '', location));
        contact.append(locationLine);
    }
    if (hasVisibleContact && !contact.childElementCount) {
        contact.append(createElement('p', 'us-person-card__contact us-person-card__contact--empty', options.noContactLabel));
    }

    const reservedColumns = new Set([
        'photoUri', 'number', 'displayName', 'credentialAbbreviations', 'contact.email',
        'contact.phone', 'contact.mobile', 'location.addressLine1', 'location.addressLine2',
        'location.locality', 'location.administrativeArea', 'location.postalCode',
        'location.country', 'status.name', 'personTags',
    ]);
    const details = createElement('dl', 'us-person-card__details');
    columns.filter(column => !reservedColumns.has(column.key)).forEach(column => {
        const value = cardValue(person, column);
        if (!value) {
            return;
        }
        const field = createElement('div', 'us-person-card__detail');
        field.append(
            createElement('dt', 'us-person-card__detail-label', column.label),
            createElement('dd', 'us-person-card__detail-value', value),
        );
        details.append(field);
    });

    const tags = personTags(person);
    const tagList = createElement('div', 'us-person-card__tags');
    if (hasCardColumn(columns, 'personTags')) {
        tags.slice(0, 3).forEach(tag => tagList.append(createElement('span', 'us-person-card__tag', tag)));
        if (tags.length > 3) {
            tagList.append(createElement('span', 'us-person-card__tag us-person-card__tag--more', `+${tags.length - 3}`));
        }
    }

    const footer = createElement('footer', 'us-person-card__footer');
    if (tagList.childElementCount) {
        footer.append(tagList);
    }
    const profileLink = createElement('a', 'us-person-card__profile-link', options.viewProfileLabel);
    profileLink.href = profileUrl;
    const arrow = createElement('i', 'bi bi-arrow-right');
    arrow.setAttribute('aria-hidden', 'true');
    profileLink.append(arrow);
    footer.append(profileLink);

    card.append(header);
    if (contact.childElementCount) {
        card.append(contact);
    }
    if (details.childElementCount) {
        card.append(details);
    }
    card.append(footer);
    return card;
}

function createOrganizationCard(organization, rowNode, options, columns) {
    const organizationId = normalizedText(organization.organizationId);
    const name = normalizedText(organization.name) || options.unnamedLabel;
    const detailsUrl = `/Organizations/Edit?id=${encodeURIComponent(organizationId)}`;
    const card = createElement('article', 'us-person-card us-organization-card');

    const selection = createElement('label', 'us-person-card__selection');
    const selectionInput = createElement('input', 'form-check-input');
    selectionInput.type = 'checkbox';
    selectionInput.setAttribute('aria-label', options.selectTemplate.replace('{0}', name));
    const rowCheckbox = rowNode?.querySelector('.row-selector input, .form-check input');
    selectionInput.checked = Boolean(rowCheckbox?.checked);
    card.classList.toggle('is-selected', selectionInput.checked);
    selectionInput.addEventListener('change', () => {
        if (rowCheckbox && rowCheckbox.checked !== selectionInput.checked) {
            rowCheckbox.click();
        }
        card.classList.toggle('is-selected', selectionInput.checked);
    });
    selection.append(selectionInput);

    const heading = createElement('div', 'us-person-card__heading');
    if (hasCardColumn(columns, 'name')) {
        const nameLink = createElement('a', 'us-person-card__name', name);
        nameLink.href = detailsUrl;
        heading.append(nameLink);
    }

    const header = createElement('header', 'us-person-card__header us-person-card__header--no-photo');
    header.append(selection);
    if (heading.childElementCount) {
        header.append(heading);
    }

    const contact = createElement('div', 'us-person-card__contacts');
    const email = normalizedText(valueAt(organization, 'contact.email'));
    const phone = normalizedText(valueAt(organization, 'contact.phone'));
    const mobile = normalizedText(valueAt(organization, 'contact.mobile'));
    const visibleLocation = {
        addressLine1: hasCardColumn(columns, 'location.addressLine1') ? organization.location?.addressLine1 : null,
        addressLine2: hasCardColumn(columns, 'location.addressLine2') ? organization.location?.addressLine2 : null,
        locality: hasCardColumn(columns, 'location.locality') ? organization.location?.locality : null,
        administrativeArea: hasCardColumn(columns, 'location.administrativeArea') ? organization.location?.administrativeArea : null,
        postalCode: hasCardColumn(columns, 'location.postalCode') ? organization.location?.postalCode : null,
        country: hasCardColumn(columns, 'location.country') ? organization.location?.country : null,
    };
    const location = personLocation({ location: visibleLocation });
    const hasVisibleContact = hasCardColumn(columns, 'contact.email')
        || hasCardColumn(columns, 'contact.phone')
        || hasCardColumn(columns, 'contact.mobile')
        || Object.values(visibleLocation).some(Boolean);
    if (email && hasCardColumn(columns, 'contact.email')) {
        contact.append(contactLink('bi-envelope', email, `mailto:${email}`));
    }
    if (phone && hasCardColumn(columns, 'contact.phone')) {
        contact.append(contactLink('bi-telephone', phone, `tel:${phone.replace(/[^+\d]/g, '')}`));
    }
    if (mobile && hasCardColumn(columns, 'contact.mobile')) {
        contact.append(contactLink('bi-phone', mobile, `tel:${mobile.replace(/[^+\d]/g, '')}`));
    }
    if (location) {
        const locationLine = createElement('p', 'us-person-card__contact');
        const glyph = createElement('i', 'bi bi-geo-alt');
        glyph.setAttribute('aria-hidden', 'true');
        locationLine.append(glyph, createElement('span', '', location));
        contact.append(locationLine);
    }
    if (hasVisibleContact && !contact.childElementCount) {
        contact.append(createElement('p', 'us-person-card__contact us-person-card__contact--empty', options.noContactLabel));
    }

    const reservedColumns = new Set([
        'name', 'contact.email', 'contact.phone', 'contact.mobile', 'location.addressLine1',
        'location.addressLine2', 'location.locality', 'location.administrativeArea',
        'location.postalCode', 'location.country', 'organizationTags',
    ]);
    const details = createElement('dl', 'us-person-card__details');
    columns.filter(column => !reservedColumns.has(column.key)).forEach(column => {
        const value = cardValue(organization, column);
        if (!value) {
            return;
        }
        const field = createElement('div', 'us-person-card__detail');
        field.append(
            createElement('dt', 'us-person-card__detail-label', column.label),
            createElement('dd', 'us-person-card__detail-value', value),
        );
        details.append(field);
    });

    const tags = organizationTags(organization);
    const tagList = createElement('div', 'us-person-card__tags');
    if (hasCardColumn(columns, 'organizationTags')) {
        tags.slice(0, 3).forEach(tag => tagList.append(createElement('span', 'us-person-card__tag', tag)));
        if (tags.length > 3) {
            tagList.append(createElement('span', 'us-person-card__tag us-person-card__tag--more', `+${tags.length - 3}`));
        }
    }

    const footer = createElement('footer', 'us-person-card__footer');
    if (tagList.childElementCount) {
        footer.append(tagList);
    }
    const detailsLink = createElement('a', 'us-person-card__profile-link', options.viewDetailsLabel);
    detailsLink.href = detailsUrl;
    const arrow = createElement('i', 'bi bi-arrow-right');
    arrow.setAttribute('aria-hidden', 'true');
    detailsLink.append(arrow);
    footer.append(detailsLink);

    card.append(header);
    if (contact.childElementCount) {
        card.append(contact);
    }
    if (details.childElementCount) {
        card.append(details);
    }
    card.append(footer);
    return card;
}

function createCaseCard(caseRecord, rowNode, options, columns) {
    const caseId = normalizedText(caseRecord.caseId);
    const referenceNumber = normalizedText(caseRecord.referenceNumber);
    const name = normalizedText(caseRecord.name) || options.unnamedLabel;
    const detailsUrl = (options.detailsUrlTemplate || '/Cases/Edit?id={id}')
        .replace('{id}', encodeURIComponent(caseId));
    const card = createElement('article', 'us-person-card us-case-card');

    const rowCheckbox = rowNode?.querySelector('.row-selector input, .form-check input');
    const selection = rowCheckbox ? createElement('label', 'us-person-card__selection') : null;
    if (selection) {
        const selectionInput = createElement('input', 'form-check-input');
        selectionInput.type = 'checkbox';
        selectionInput.setAttribute('aria-label', options.selectTemplate.replace('{0}', referenceNumber || name));
        selectionInput.checked = rowCheckbox.checked;
        card.classList.toggle('is-selected', selectionInput.checked);
        selectionInput.addEventListener('change', () => {
            if (rowCheckbox.checked !== selectionInput.checked) {
                rowCheckbox.click();
            }
            card.classList.toggle('is-selected', selectionInput.checked);
        });
        selection.append(selectionInput);
    }

    const heading = createElement('div', 'us-person-card__heading');
    if (referenceNumber && hasCardColumn(columns, 'referenceNumber')) {
        heading.append(createElement('p', 'us-person-card__number', referenceNumber));
    }
    if (hasCardColumn(columns, 'name')) {
        const nameLink = createElement('a', 'us-person-card__name', name);
        nameLink.href = detailsUrl;
        heading.append(nameLink);
    }
    const status = normalizedText(valueAt(caseRecord, 'statusName'));
    if (status && hasCardColumn(columns, 'statusName')) {
        heading.append(createElement('span', 'us-person-card__status us-case-card__status', status));
    }

    const header = createElement('header', 'us-person-card__header us-person-card__header--no-photo');
    if (selection) {
        header.append(selection);
    }
    if (heading.childElementCount) {
        header.append(heading);
    }

    const reservedColumns = new Set(['referenceNumber', 'name', 'statusName', 'caseTags']);
    const details = createElement('dl', 'us-person-card__details');
    columns.filter(column => !reservedColumns.has(column.key)).forEach(column => {
        const value = cardValue(caseRecord, column);
        if (!value) {
            return;
        }
        const field = createElement('div', 'us-person-card__detail');
        field.append(
            createElement('dt', 'us-person-card__detail-label', column.label),
            createElement('dd', 'us-person-card__detail-value', value),
        );
        details.append(field);
    });

    const tags = caseTags(caseRecord);
    const tagList = createElement('div', 'us-person-card__tags');
    if (hasCardColumn(columns, 'caseTags')) {
        tags.slice(0, 3).forEach(tag => tagList.append(createElement('span', 'us-person-card__tag', tag)));
        if (tags.length > 3) {
            tagList.append(createElement('span', 'us-person-card__tag us-person-card__tag--more', `+${tags.length - 3}`));
        }
    }

    const footer = createElement('footer', 'us-person-card__footer');
    if (tagList.childElementCount) {
        footer.append(tagList);
    }
    const detailsLink = createElement('a', 'us-person-card__profile-link', options.viewDetailsLabel);
    detailsLink.href = detailsUrl;
    const arrow = createElement('i', 'bi bi-arrow-right');
    arrow.setAttribute('aria-hidden', 'true');
    detailsLink.append(arrow);
    footer.append(detailsLink);

    card.append(header);
    if (details.childElementCount) {
        card.append(details);
    }
    card.append(footer);
    return card;
}

function readStoredView(key) {
    try {
        const value = window.localStorage.getItem(key);
        return value === CARD_VIEW ? CARD_VIEW : ROW_VIEW;
    } catch {
        return ROW_VIEW;
    }
}

function storeView(key, view) {
    try {
        window.localStorage.setItem(key, view);
    } catch {
        // Personalization remains available for the current page when storage is blocked.
    }
}

function refreshResponsiveLayout(table) {
    window.requestAnimationFrame(() => {
        const api = dataTableApi(table);
        api?.columns?.adjust?.();
        api?.responsive?.recalc?.();
    });
}

function positionCardView(workspace, cardView) {
    const dataSurface = workspace.querySelector('.us-data-surface');
    if (!dataSurface) {
        return;
    }

    const tableLayout = dataSurface.querySelector('.dt-layout-table');
    if (tableLayout) {
        if (cardView.nextElementSibling !== tableLayout) {
            tableLayout.before(cardView);
        }
        return;
    }

    if (cardView.nextElementSibling !== dataSurface) {
        dataSurface.before(cardView);
    }
}

function enhanceCardDirectory(root, config) {
    if (root.dataset[config.enhancedDatasetKey] === 'true') {
        return;
    }

    root.querySelectorAll('.us-people-directory__summary').forEach(summary => summary.remove());

    const workspace = root.querySelector('[data-us-datatable-workspace]');
    const table = workspace?.querySelector('table.data-table');
    const cardView = root.querySelector(config.cardViewSelector);
    const cardGrid = root.querySelector(config.cardGridSelector);
    const empty = root.querySelector(config.emptySelector);
    if (!workspace || !table || !cardView || !cardGrid || !empty) {
        return;
    }

    root.dataset[config.enhancedDatasetKey] = 'true';
    cardView.dataset.usDatatableObserverIgnore = '';
    positionCardView(workspace, cardView);

    const options = config.options(root);
    const storageKey = root.dataset.usViewStorageKey || 'unionstack:people-view';
    let preferredView = readStoredView(storageKey);
    const mobileViewQuery = config.autoCardViewOnMobile && typeof window.matchMedia === 'function'
        ? window.matchMedia(MOBILE_CARD_VIEW_QUERY)
        : null;

    const render = () => {
        const records = tableRecords(table);
        const columns = visibleCardColumns(table);
        cardGrid.replaceChildren(...records.map(record => config.createCard(record.data || {}, record.node, options, columns)));
        empty.hidden = records.length > 0;
        cardGrid.hidden = records.length === 0;

    };

    const setView = (view, persist = true) => {
        const nextView = view === CARD_VIEW ? CARD_VIEW : ROW_VIEW;
        root.dataset[config.viewDatasetKey] = nextView;
        cardView.hidden = nextView !== CARD_VIEW;
        root.querySelectorAll(`[${config.viewAttribute}]`).forEach(button => {
            button.setAttribute('aria-pressed', String(button.getAttribute(config.viewAttribute) === nextView));
        });
        if (nextView === CARD_VIEW) {
            positionCardView(workspace, cardView);
            render();
        } else {
            refreshResponsiveLayout(table);
        }
        if (persist) {
            preferredView = nextView;
            storeView(storageKey, nextView);
        }
    };

    root.querySelectorAll(`[${config.viewAttribute}]`).forEach(button => {
        button.addEventListener('click', () => setView(button.getAttribute(config.viewAttribute)));
    });
    root.addEventListener('unionstack:datatable:updated', () => {
        positionCardView(workspace, cardView);
        render();
    });
    window.jQuery?.(table)?.on?.(
        'column-visibility.dt.unionstackPeople column-reorder.dt.unionstackPeople',
        () => window.requestAnimationFrame(render),
    );
    workspace.addEventListener('change', event => {
        if (event.target instanceof HTMLInputElement && event.target.matches('.form-check-input')) {
            window.requestAnimationFrame(render);
        }
    });

    const body = table.tBodies[0];
    if (body) {
        new MutationObserver(() => window.requestAnimationFrame(render))
            .observe(body, { childList: true, subtree: true });
    }

    render();
    const applyResponsiveView = () => setView(
        mobileViewQuery?.matches ? CARD_VIEW : preferredView,
        false,
    );
    if (typeof mobileViewQuery?.addEventListener === 'function') {
        mobileViewQuery.addEventListener('change', applyResponsiveView);
    } else {
        mobileViewQuery?.addListener?.(applyResponsiveView);
    }
    applyResponsiveView();
}

function enhancePeopleDirectory(root) {
    enhanceCardDirectory(root, {
        autoCardViewOnMobile: true,
        enhancedDatasetKey: 'usPeopleDirectoryEnhanced',
        viewDatasetKey: 'usPeopleView',
        viewAttribute: 'data-us-people-view',
        cardViewSelector: '[data-us-people-card-view]',
        cardGridSelector: '[data-us-people-card-grid]',
        emptySelector: '[data-us-people-empty]',
        createCard: createPersonCard,
        options: directory => ({
            noContactLabel: directory.dataset.usNoContact || globalThis.UnionStackI18n.text("People.NoContactDetails"),
            selectTemplate: directory.dataset.usSelectPerson || 'Select {0}',
            unnamedLabel: window.UnionStackUi?.text?.unknown || 'Unnamed person',
            viewProfileLabel: directory.dataset.usViewProfile || 'View profile',
        }),
    });
}

function enhanceOrganizationDirectory(root) {
    enhanceCardDirectory(root, {
        enhancedDatasetKey: 'usOrganizationDirectoryEnhanced',
        viewDatasetKey: 'usOrganizationView',
        viewAttribute: 'data-us-organization-view',
        cardViewSelector: '[data-us-organization-card-view]',
        cardGridSelector: '[data-us-organization-card-grid]',
        emptySelector: '[data-us-organization-empty]',
        createCard: createOrganizationCard,
        options: directory => ({
            noContactLabel: directory.dataset.usNoContact || globalThis.UnionStackI18n.text("People.NoContactDetails"),
            selectTemplate: directory.dataset.usSelectOrganization || 'Select {0}',
            unnamedLabel: window.UnionStackUi?.text?.unknown || 'Unnamed organization',
            viewDetailsLabel: directory.dataset.usViewDetails || 'View details',
        }),
    });
}

function enhanceCaseDirectory(root) {
    enhanceCardDirectory(root, {
        enhancedDatasetKey: 'usCaseDirectoryEnhanced',
        viewDatasetKey: 'usCaseView',
        viewAttribute: 'data-us-case-view',
        cardViewSelector: '[data-us-case-card-view]',
        cardGridSelector: '[data-us-case-card-grid]',
        emptySelector: '[data-us-case-empty]',
        createCard: createCaseCard,
        options: directory => ({
            selectTemplate: directory.dataset.usSelectCase || 'Select {0}',
            unnamedLabel: window.UnionStackUi?.text?.unknown || 'Unnamed case',
            viewDetailsLabel: directory.dataset.usViewDetails || 'View details',
            detailsUrlTemplate: directory.dataset.usCaseDetailsUrl || '/Cases/Edit?id={id}',
        }),
    });
}

function enhancePeopleDirectories(scope = document) {
    scope.querySelectorAll('[data-us-people-directory]').forEach(enhancePeopleDirectory);
    scope.querySelectorAll('[data-us-organization-directory]').forEach(enhanceOrganizationDirectory);
    scope.querySelectorAll('[data-us-case-directory]').forEach(enhanceCaseDirectory);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhancePeopleDirectories(), { once: true });
} else {
    enhancePeopleDirectories();
}

export {
    createCaseCard,
    createOrganizationCard,
    createPersonCard,
    enhanceCaseDirectory,
    enhanceOrganizationDirectory,
    enhancePeopleDirectories,
    enhancePeopleDirectory,
    positionCardView,
    tableRecords,
    visibleCardColumns,
};
